import { memo } from 'react';
import { Controller } from 'react-hook-form';
import { DateTime } from 'luxon';
import { FormattedMessage, useIntl } from 'react-intl';
import { FormField, DateInput } from '@library';
import type { DatepickerComponent } from '../../../types/customStoryblokComponents';
import { DEFAULT_DATE_TIME_FORMAT } from '../../../../core/constants';

interface DatepickerProps {
    params: DatepickerComponent;
}

const StoryblockFormat = 'yyyy-LL-dd HH:mm';

const getMinDate = (onlyFutureDate: boolean, minDate?: string) => {
    if (onlyFutureDate) {
        const tomorrow = DateTime.now().plus({ days: 1 });
        return tomorrow.toJSDate();
    }

    return minDate
        ? DateTime.fromFormat(minDate, StoryblockFormat).toJSDate()
        : undefined;
};

const getMaxDate = (onlyFutureDate: boolean, maxDate?: string) => {
    if (onlyFutureDate) {
        return undefined;
    }

    return maxDate
        ? DateTime.fromFormat(maxDate, StoryblockFormat).toJSDate()
        : undefined;
};

const DatePicker = memo(
    ({
        params: {
            label,
            name,
            required,
            placeholder,
            _uid,
            onlyFutureDate,
            maxDate: _maxDate,
            minDate: _minDate,
        },
    }: DatepickerProps) => {
        const intl = useIntl();
        const minDate = getMinDate(onlyFutureDate, _minDate);
        const maxDate = getMaxDate(onlyFutureDate, _maxDate);

        return (
            <Controller
                name={name || _uid}
                render={({ field, fieldState: { error } }) => {
                    return (
                        <FormField
                            label={label}
                            error={
                                error?.message ? (
                                    <FormattedMessage id={error.message} />
                                ) : undefined
                            }
                        >
                            <div tabIndex={0} ref={field.ref}>
                                <DateInput
                                    disabled={field.disabled}
                                    value={
                                        field.value
                                            ? DateTime.fromFormat(
                                                  field.value,
                                                  DEFAULT_DATE_TIME_FORMAT
                                              ).toJSDate()
                                            : undefined
                                    }
                                    onChange={date =>
                                        field.onChange(
                                            date
                                                ? DateTime.fromJSDate(
                                                      date
                                                  ).toFormat(
                                                      DEFAULT_DATE_TIME_FORMAT
                                                  )
                                                : undefined
                                        )
                                    }
                                    calendarProps={{
                                        minDate,
                                        maxDate,
                                    }}
                                    required={required}
                                    placeholder={
                                        placeholder ||
                                        intl.formatMessage({
                                            id: 'select date',
                                        })
                                    }
                                />
                            </div>
                        </FormField>
                    );
                }}
                rules={{
                    required: required ? 'this field is required' : false,
                }}
            />
        );
    }
);

export default memo(DatePicker);
